// React imports
import React, { useEffect, useMemo, useState } from 'react';

// 3rd party library imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faPenToSquare, faPlusCircle, faSearchPlus } from '@fortawesome/pro-solid-svg-icons';
import { Toast, ToastContainer } from 'react-bootstrap';
import subMonths from 'date-fns/subMonths';
import Bugsnag from '@bugsnag/js';

// Static data imports
import { CountryList } from '../../assets/staticData/CountryList';

// Component imports
import AddCompanyModal from './AddCompanyModal/AddCompanyModal';
import AutoRiskScoreModalCompany from './AutoRiskScoreModal';
import Button from '../../ui-elements/Button/Button';
import Checkbox from '../../ui-elements/Input/Checkbox';
import DropSelect from '../../ui-elements/DropSelect/DropSelect';
import EditContactModal from './EditContactModal/EditContactModal';
import Input from '../../ui-elements/Input/Input';
import Label, { LabelTypes } from '../../ui-elements/Label/Label';
import Link from '../../ui-elements/Link/Link';
import RiskScoreModal from './RiskScoreModal/RiskScoreModal';
import SendNotificationModal from './SendNotificationModal';
import Table from '../../ui-elements/Table/Table';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import Alert, { AlertType } from '../../ui-elements/Alert/Alert';

// Service imports
import { GET_KELDAN_API_URL, TOAST_DELAY } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { KnowYourCustomer, ServiceCompanyEmployee, LastAnsweredFilter, RiskScoreFilter, StatusFilter, RiskScoreFormValues, ContactsOverview } from '../../types/DueDiligenceTypes';
import { getTableDate, insertDash, isSSN, translateSurveyStatus } from '../../services/utils';

// Type imports
import { Fetched } from '../../types/Types';
import GetKYCOverviewCompaniesModal from './GetKYCOverview/GetKYCOverviewCompaniesModal';


interface IProps {
    customers: Fetched<KnowYourCustomer[]>;
    setCustomers: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    setShowKYCOverview: React.Dispatch<React.SetStateAction<boolean>>;
    companyKYCPrice: number;
    employees: ServiceCompanyEmployee[];
    showKYCOverviewModal: boolean;
}

export const riskToLabelTypeMap: { [r in string]: LabelTypes } = {
    'Lítil': 'low',
    'Meðal': 'medium',
    'Mikil': 'high'
}

const CompaniesTable: React.FC<IProps> = ({
    customers,
    setCustomers,
    setShowKYCOverview,
    companyKYCPrice,
    employees,
    showKYCOverviewModal
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<KnowYourCustomer[]>();

    // Filter states
    const [lastAnsweredFilter, setLastAnsweredFilter] = useState<LastAnsweredFilter>('all');
    const [riskScoreFilter, setRiskScoreFilter] = useState<RiskScoreFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [supervisorFilter, setSupervisorFilter] = useState<ServiceCompanyEmployee | null>(null);

    const [addCompanyShow, setAddCompanyModal] = React.useState<boolean>(false);

    const [editContactModal, setEditContactModal] = useState<{
        isOpen: boolean;
        data: { customerId: number; companyName: string; confirmProcurations: boolean; supervisorWebUserId?: number | null; contactEmail?: string | null };
        contacts: ContactsOverview | null;
    }>({
        isOpen: false,
        data: { customerId: 0, companyName: '', confirmProcurations: false, supervisorWebUserId: null, contactEmail: null },
        contacts: null
    });

    const [sendNotificationModal_isOpen, setSendNotificationModal_isOpen] = useState(false);

    const [riskScoreModal_isOpen, setRiskScoreModal_isOpen] = useState(false);
    const [riskScoreModal_data, setRiskScoreModal_data] = useState<RiskScoreFormValues | undefined>(undefined);

    const [autoRiskScoreModel_isOpen, setAutoRiskScoreModel_isOpen] = useState(false);

    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>("success");
    const [toastMessage, setToastMessage] = useState("");
    const [excelDownloading, setExcelDownloading] = useState(false);

    const fetchCompanies = async () => {
        try {
            setIsLoading(true);
            const url = `${GET_KELDAN_API_URL()}/DueDiligence/GetCustomerOverview`;

            const response = await fetch(url, {
                cache: 'no-store'
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();
            setCustomers(data.map((c: KnowYourCustomer) => ({ ...c, checked: false })));
        } catch (error) {
            setCustomers(new Error(ErrorMessages.NetworkError));
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            // Only refresh if the page was loaded from cache (back button used)
            if (event.persisted) {
                // Fetch fresh data from the server to reflect any changes
                // that may have occurred while the user was on other pages
                fetchCompanies();
            }
        };

        // Listen for the pageshow event on the window
        window.addEventListener('pageshow', handlePageShow);

        // Clean up the event listener when component unmounts
        // This prevents memory leaks and duplicate listeners
        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []); // Empty dependency array means this effect runs once on mount

    // Apply all filters when dropdown filters change
    useEffect(() => {
        // Don't show loading indicator for dropdown filter changes
        // as they should be nearly instant

        // Skip the effect if data isn't loaded yet
        if (!customers || customers instanceof Error) return;

        // Use the direct filtering approach to avoid stale closures
        const filtered = applyFiltersDirectly(
            [...customers],
            searchTerm,
            lastAnsweredFilter,
            riskScoreFilter,
            statusFilter,
            supervisorFilter
        );

        setFilteredData(filtered);
    }, [customers, searchTerm, lastAnsweredFilter, riskScoreFilter, statusFilter, supervisorFilter]);

    // Debounced search handler (with loading indicator)
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const anyChecked = useMemo(() => {
        if (!filteredData || filteredData instanceof Error) return false;
        return filteredData.some(c => c.checked);
    }, [filteredData]);

    const onlyAutoEligibleChecked = useMemo(() => {
        if (!filteredData || filteredData instanceof Error) return false;
        const checkedCustomers = filteredData.filter(c => c.checked);
        return checkedCustomers.every(c => c.questionsAnswered && c.riskscore === null) && checkedCustomers.length > 0;
    }, [filteredData]);

    const onlyNotificationEligibleChecked = useMemo(() => {
        if (!filteredData || filteredData instanceof Error) return false;
        const checkedCustomers = filteredData.filter(c => c.checked);
        return checkedCustomers.every(c => (!c.questionsAnswered || c.status === 'Tengilið breytt') && c.riskscoreId && c.contactName && c.status !== 'Villa') && checkedCustomers.length > 0;
    }, [filteredData]);

    // Handler to add a new customer
    const handleAddCustomer = (newCustomer: KnowYourCustomer) => {
        setCustomers(prev => !(prev instanceof Error) && prev !== null ? [newCustomer, ...prev] : [newCustomer]);
    };

    // Shared function to apply filters and update state
    const updateFiltersAndState = (currentSearchTerm: string) => {
        if (!customers || customers instanceof Error) {
            // Handle error state
            setFilteredData([]);
            return;
        }
        // Create a local copy of the data to filter
        const dataToFilter = [...(customers || [])];

        // Force immediate filtering with current state values
        const filtered =
            dataToFilter.length > 0
                ? applyFiltersDirectly(
                    dataToFilter,
                    currentSearchTerm,
                    lastAnsweredFilter,
                    riskScoreFilter,
                    statusFilter,
                    supervisorFilter
                )
                : [];
        // Update filtered data state
        setFilteredData(filtered);

        // Turn off loading state
        setIsLoading(false);
    };

    const applyFiltersDirectly = (
        data: KnowYourCustomer[],
        search: string,
        lastAnswered: LastAnsweredFilter,
        riskScore: RiskScoreFilter,
        status: StatusFilter,
        supervisor: ServiceCompanyEmployee | null
    ): KnowYourCustomer[] => {
        let result = [...data];

        // Apply search term filter ONLY if it's not empty and has sufficient length
        if (search.trim() !== '' && search.length >= 2) {
            const searchTermLower = search.toLowerCase().trim();
            result = result.filter((item) => {
                // Search through name fields
                if (item.companyName.toLowerCase().includes(searchTermLower)) return true;

                // Search through ID
                const companyId = item.companySSN;
                if (isSSN(companyId)) {
                    if (companyId.includes(searchTermLower)) return true;
                }

                return false;
            });
        }

        if (lastAnswered !== 'all') {
            const now = new Date();
            const threeMonthsAgo = new Date(now.setMonth(now.getMonth() - 3));
            const eighteenMonthsAgo = new Date(now.setMonth(now.getMonth() - 18));
            const twentyFourMonthsAgo = new Date(now.setMonth(now.getMonth() - 24));

            result = result.filter(item => {
                const lastAnsweredDate = item.questionsAnswered && item.answeredTimestamp
                    ? new Date(item.answeredTimestamp)
                    : null;
                switch (lastAnswered) {
                    case 'exists':
                        return item.questionsAnswered;
                    case '3M':
                        return lastAnsweredDate !== null && lastAnsweredDate >= threeMonthsAgo;
                    case '18M':
                        return lastAnsweredDate !== null && lastAnsweredDate >= eighteenMonthsAgo;
                    case '24M':
                        return lastAnsweredDate !== null && lastAnsweredDate >= twentyFourMonthsAgo;
                    case 'never':
                        return !item.questionsAnswered;
                    default:
                        return true;
                }
            });
        }

        if (riskScore !== 'all') {
            result = result.filter(item => {
                const riskScoreValue = item.riskscore ? item.riskscore.split(' ')[0] : '';
                switch (riskScore) {
                    case 'low':
                        return riskScoreValue === 'Lítil';
                    case 'medium':
                        return riskScoreValue === 'Meðal';
                    case 'high':
                        return riskScoreValue === 'Mikil';
                    case 'unfinished':
                        return !item.riskscore;
                    default:
                        return true;
                }
            });
        }

        if (status !== 'all') {
            result = result.filter(item => {
                const statusValue = item.status || '';
                switch (status) {
                    case 'started':
                        return statusValue === 'Könnun hafin' || statusValue === 'KYC skýrsla sótt';
                    case 'contactSet':
                        return statusValue === 'Tengilið breytt';
                    case 'sent':
                        return statusValue === 'Tilkynning send';
                    case 'answered':
                        return statusValue === 'Könnun svarað';
                    case 'riskDetermined':
                        return statusValue === 'Áhættumat lokið';
                    case 'error':
                        return statusValue === 'Villa';
                    default:
                        return true;
                }
            });
        }

        if (supervisor !== null) {
            result = result.filter(item => item.supervisorSSN === supervisor.ssn);
        }

        return result;
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set the new search term immediately
        setSearchTerm(value);

        // Show loading indicator for search
        setIsLoading(true);

        // Debounce the filter application
        searchTimeoutRef.current = setTimeout(() => {
            updateFiltersAndState(value);
        }, 500);
    };

    const handleLastAnsweredFilterChange = (option: string) => {
        setLastAnsweredFilter(option as LastAnsweredFilter);
    };

    const handleRiskScoreFilterChange = (option: string) => {
        setRiskScoreFilter(option as RiskScoreFilter);
    };

    const handleStatusFilterChange = (option: string) => {
        setStatusFilter(option as StatusFilter);
    };

    const handleSupervisorFilterChange = (option: string) => {
        if (option === "all") {
            setSupervisorFilter(null);
        } else {
            const supervisor = employees.find(e => e.ssn === option);
            setSupervisorFilter(supervisor ?? null);
        }
    };

    const handleCheckboxChange = (customer: KnowYourCustomer, checked: boolean) => {
        setCustomers(prev => (!prev || prev instanceof Error) ? [customer] : prev.map(c =>
            c.companyId === customer.companyId ? { ...c, checked } : c
        ));
    };

    const checkAll = (checked: boolean) => {
        // only check visible/filtered customers
        setCustomers(prev => {
            if (!prev || prev instanceof Error) return [];
            return prev.map(c => {
                if (filteredData && filteredData.find(fd => fd.companyId === c.companyId)) {
                    return { ...c, checked };
                }
                return c;
            });
        });
    };

    const displayControlPanel = () => {
        return (
            <div className="control-panel">
                <Button disabled={!anyChecked} onClick={() => setShowKYCOverview(true)}>
                    Hefja könnun
                </Button>
                <Button disabled={!onlyNotificationEligibleChecked} onClick={() => setSendNotificationModal_isOpen(true)}>
                    Senda könnun
                </Button>
                <Button disabled={!onlyAutoEligibleChecked} onClick={() => setAutoRiskScoreModel_isOpen(true)}>Sjálfvirkt áhættumat</Button>
            </div>
        )
    }

    const fetchContacts = async (customerId: number) => {
        try {
            const response = await fetch(`/DueDiligence/GetKycContacts/${customerId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                setToastType("error");
                setToastMessage("Ekki tókst að sækja tengiliði");
                setToastShow(true);
            }

            const data: ContactsOverview = await response.json();
            return data;
        } catch (err) {
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage("Ekki tókst að sækja tengiliði");
            setToastShow(true);
            return null;
        }
    }

    const handleOpenEditContactModal = async (item: KnowYourCustomer) => {
        try {
            // Fetch contacts for the selected company
            const contacts = await fetchContacts(item.customerId);

            // Set all modal data at once, including opening the modal
            // This ensures data is ready when the modal renders
            setEditContactModal({
                isOpen: true,
                data: {
                    customerId: item.customerId,
                    companyName: item.companyName,
                    confirmProcurations: item.confirmProcurations ?? false,
                    supervisorWebUserId: (item as any).supervisorWebUserId ?? null,
                    contactEmail: item.contactEmail ?? null
                },
                contacts: contacts
            });
        } catch (error) {
            // Handle errors from fetchContacts
            console.error('Failed to fetch contacts:', error);
            setToastType("error");
            setToastMessage("Ekki tókst að sækja tengiliði");
            setToastShow(true);
        }
    };

    const handleCloseEditContactModal = () => {
        setEditContactModal({
            isOpen: false,
            data: { customerId: 0, companyName: '', confirmProcurations: false },
            contacts: null
        });
    };

    const handleDownloadExcel = async () => {
        try {
            setExcelDownloading(true);
            // Recompute filtered source if state not ready
            const source: KnowYourCustomer[] = (filteredData && filteredData.length > 0)
                ? filteredData
                : (!customers || customers instanceof Error
                    ? []
                    : applyFiltersDirectly(
                        [...customers],
                        searchTerm,
                        lastAnsweredFilter,
                        riskScoreFilter,
                        statusFilter,
                        supervisorFilter
                    ));

            if (source.length === 0) {
                setToastType('error');
                setToastMessage('Engin gögn til að sækja');
                setToastShow(true);
                return;
            }

            const ids = source.map(c => c.customerId);
            const response = await fetch('/DueDiligence/GetCompanyOverviewExcel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ids)
            });

            if (!response.ok) {
                setToastType('error');
                setToastMessage('Ekki tókst að sækja Excel skrá');
                setToastShow(true);
                return;
            }

            // If backend returns no content (edge case)
            if (response.status === 204) {
                setToastType('error');
                setToastMessage('Excel skrá skilaði engu');
                setToastShow(true);
                return;
            }

            // Create a blob from the response and trigger download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'KYC_Fyrirtaeki_Yfirlit.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType('error');
            setToastMessage('Villa kom upp við að sækja Excel skrá');
            setToastShow(true);
        } finally {
            setExcelDownloading(false);
        }
    };

    return (
        <>
            <div className="filter-wrapper">
                {/* Search input */}
                <Input
                    onChange={handleSearchChange}
                    value={searchTerm}
                    className="input-field"
                    label="Leita"
                    clearInput
                    withSearchGlassIcon
                />
                {/* Last answered filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Kannanir"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Til staðar", value: "exists" },
                        { label: "Yngri en 3 mánaða", value: "3M" },
                        { label: "Eldri en 18 mánaða", value: "18M" },
                        { label: "Eldri en 24 mánaða", value: "24M" },
                        { label: "Ekki til staðar", value: "never" }
                    ]}
                    defaultValue="all"
                    onChange={handleLastAnsweredFilterChange}
                    size="lg"
                />
                {/* Risk score filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Áhættumat"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Lítil áhætta", value: "low" },
                        { label: "Meðal áhætta", value: "medium" },
                        { label: "Mikil áhætta", value: "high" },
                        { label: "Ólokið", value: "unfinished" },
                    ]}
                    defaultValue="all"
                    onChange={handleRiskScoreFilterChange}
                    size="lg"
                />
                {/* Status filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Staða"
                    options={[
                        { label: "Sýna allt", value: "all" },
                        { label: "Könnun hafin", value: "started" },
                        { label: "Könnun send", value: "sent" },
                        { label: "Könnun svarað", value: "answered" },
                        { label: "Tengilið breytt", value: "contactSet" },
                        { label: "Áhættumat lokið", value: "riskDetermined" },
                        { label: "Villa", value: "error" },
                    ]}
                    defaultValue="all"
                    onChange={handleStatusFilterChange}
                    size="lg"
                />
                {/* Supervisor filter */}
                <DropSelect
                    className="dropdown-field"
                    label="Umsjónaraðili"
                    options={[{ label: "Sýna allt", value: "all" }, ...employees.map(e => ({ label: e.fullname, value: e.ssn, disabledOption: !e.ssn }))]}
                    defaultValue="all"
                    onChange={handleSupervisorFilterChange}
                    size="lg"
                />
            </div>
            {/* Table display */}
            {customers instanceof Error
                ? <Alert type="error" headText={customers.message} />
                : <Table
                    emptyTableText={customers?.length === 0 ? "Engir lögaðilar fundust. Þú getur bætt við lögaðila með hnappinum hér fyrir ofan." : "Engir lögaðilar uppfylla skilyrði síunnar."}
                    showLoading={isLoading}
                    showEmptyTableText={true}
                    tableSize="lg"
                    withPagination
                    aSide={displayControlPanel()}
                    bSide={
                        <>
                            <Link
                                onClick={handleDownloadExcel}
                                icon='excel'
                                disabled={excelDownloading || !(filteredData && filteredData.length > 0)}
                            >
                                Sækja í Excel
                            </Link>
                            <Button
                                buttonType="secondary"
                                onClick={() => {
                                    setAddCompanyModal(true);
                                }}
                                icon={faPlusCircle}
                            >
                                Bæta við lögaðila
                            </Button>
                        </>
                    }
                    data={filteredData || (!customers || customers instanceof Error ? [] : customers)}
                    columns={[{
                        title: (
                            <Checkbox
                                name="checkAllCustomers"
                                id="checkAllCustomers"
                                disabled={filteredData?.length === 0}
                                value={filteredData?.every(c => c.checked).toString()}
                                onClick={() => checkAll(!filteredData?.every(c => c.checked))
                                }
                            />
                        ),
                        renderCell: (row: KnowYourCustomer) => (
                            <Checkbox
                                name="kyc-checkbox"
                                id={`kyc-checkbox-company-${row.companyId}`}
                                value={(row.checked || false).toString()}
                                onClick={() =>
                                    handleCheckboxChange(row, !row.checked)
                                }
                            />
                        ),
                        textAlign: 'left'
                    }, {
                        title: 'Nafn',
                        renderCell: ({ companyName, customerId, riskscoreId, status }) => <Link disabled={status === "Villa"} url={`/Areidanleikakannanir/Skoda-Svor-Logadilar?riskscoreId=${riskscoreId}&customerId=${customerId}`}><FontAwesomeIcon icon={faSearchPlus} className='fa-icon-search' />{companyName}</Link>,
                        textAlign: 'left',
                        simpleSortable: ({ companyName }) => companyName
                    }, {
                        title: 'Kennitala',
                        renderCell: ({ companySSN, companyCountry }) => {
                            if (companySSN) {
                                return insertDash(companySSN);
                            }

                            if (companyCountry) {
                                const countryData = CountryList.find(c => c.value === companyCountry);
                                return countryData ? countryData.label : '';
                            }
                        },
                        textAlign: 'left'
                    }, {
                        title: 'Tengiliður',
                        renderCell: (item) => {
                            // if we have either contactId or riskscoreId, show the contact name (when excel adding, we might have set the contact, but not yet have a riskscore)
                            if (item.contactId !== null || item.riskscoreId) {
                                return <div>
                                    {/* Only show the edit button if we have a riskscoreId */}
                                    {item.riskscoreId &&
                                        <button
                                            className="edit-info-button"
                                            onClick={() => handleOpenEditContactModal(item)}
                                        >
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </button>
                                    }

                                    {item.contactName?.split(' ')[0] || item.contactName}
                                </div>
                            }

                            return null;
                        },
                        textAlign: 'left',
                        simpleSortable: ({ contactName }) => contactName === "" ? null : contactName.toLocaleLowerCase(),
                        sortConfig: {
                            nullHandling: 'always-end'
                        }
                    },
                    {
                        title: 'Könnun hafin',
                        renderCell: ({ riskscoreCreated }) => {
                            if (riskscoreCreated) {
                                const date = new Date(riskscoreCreated);
                                return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                            }
                            return '';
                        },
                        textAlign: 'left',
                        simpleSortable: ({ riskscoreCreated }) => riskscoreCreated ? new Date(riskscoreCreated).getTime() : 0
                    },
                    {
                        title: 'Könnun svarað',
                        renderCell: ({ answeredTimestamp }) => {
                            if (answeredTimestamp) {
                                const date = new Date(answeredTimestamp);
                                let tooltipText: string = '';
                                let tooltipComp = null;
                                if (date < subMonths(new Date(), 24)) {
                                    tooltipText = 'Áreiðanleikakönnun er eldri en 24 mánaða.';
                                    tooltipComp = <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#DB0033', marginLeft: '5px' }} />;
                                } else if (date < subMonths(new Date(), 18)) {
                                    tooltipText = 'Áreiðanleikakönnun er eldri en 18 mánaða.';
                                    tooltipComp = <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#F8C513', marginLeft: '5px' }} />;
                                }
                                return isNaN(date.getTime()) ? '' : <div>{getTableDate(date, "DD/MM/YYYY", ".")}
                                    {tooltipComp && <Tooltip text={tooltipComp} noUnderline tooltip={tooltipText} />}</div>;
                            }
                            return '';
                        },
                        textAlign: 'left',
                        simpleSortable: ({ answeredTimestamp }) => answeredTimestamp ? new Date(answeredTimestamp).getTime() : 0
                    }, {
                        title: 'Áhættumat',
                        renderCell: (item) => {
                            if (!item.questionsAnswered) return null;
                            const displayString = item.riskscore ? item.riskscore.split(' ')[0] : null;
                            let labelType = displayString ? riskToLabelTypeMap[displayString] : null;
                            return <div>
                                <button
                                    className="edit-info-button"
                                    onClick={() => {
                                        setRiskScoreModal_data({
                                            riskscoreId: item.riskscoreId ?? null,
                                            riskScore: item.riskscore,
                                            description: item.riskscoreDescription,
                                            name: item.companyName
                                        });
                                        setRiskScoreModal_isOpen(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                {displayString && labelType && (
                                    item.riskscoreDescription === null || item.riskscoreDescription === ''
                                        ? <Label
                                            labelType={labelType}
                                            text={displayString}
                                            size="lg"
                                        /> : <Tooltip text={<Label
                                            labelType={labelType}
                                            text={displayString}
                                            size="lg"
                                        />} noUnderline tooltip={item.riskscoreDescription} />

                                )}
                            </div>
                        },
                        textAlign: 'left',
                        // Define how to extract the sortable value from each row
                        simpleSortable: ({ riskscore }) => {
                            // Return null if there's no riskscore
                            if (!riskscore) return null;

                            // Extract the risk level text from the riskscore string
                            const riskLevel = riskscore.split(' ')[0];

                            // Map Icelandic risk levels to numeric values for proper sorting
                            // Higher numbers = higher risk
                            const riskLevelMap: Record<string, number> = {
                                'Mikil': 3,   // High risk
                                'Meðal': 2,   // Medium risk
                                'Lítil': 1    // Low risk
                            };

                            // Return the numeric value, or null if the risk level isn't recognized
                            return riskLevelMap[riskLevel] ?? null;
                        },
                        // Configure sorting behavior: nulls always go to the end
                        sortConfig: {
                            nullHandling: 'always-end'
                        }
                    }, {
                        title: 'Staða',
                        renderCell: ({ status }) => {
                            return translateSurveyStatus(status);
                        },
                        textAlign: 'left',
                        simpleSortable: ({ status }) => {
                            if (!status || status === '' || status === null || status === undefined) {
                                return 0;
                            }

                            // Map the status to the display value for consistent sorting
                            // This ensures sorting matches what the user sees
                            if (status === "Tilkynning send") {
                                return 'Könnun send';
                            } else if (status === "KYC skýrsla sótt") {
                                return 'Könnun hafin';
                            }

                            // Return the original status for any other values
                            return status;
                        }
                    },
                    // TODO: ADD THIS BACK LATER WHEN READY
                    // {
                    //     title: 'PEP vöktun',
                    //     renderCell: ({ answeredTimestamp }) => {
                    //         return <Button size='sm'>Vakta</Button>;
                    //     },
                    //     textAlign: 'center'
                    // },

                    {
                        title: 'Allar kannanir',
                        renderCell: ({ riskscoreId, customerId }) => (
                            riskscoreId
                                ? <Button buttonType="secondary" size="sm" anchorProps={{ href: `/Areidanleikakannanir/Eldri-Gogn?customerId=${customerId}&isPerson=false` }}>Skoða</Button>
                                : ''
                        ),
                        textAlign: 'center'
                    }]}
                />
            }
            {/* Add Company Modal */}
            {
                handleAddCustomer && (
                    <AddCompanyModal
                        employees={employees}
                        show={addCompanyShow}
                        close={() => setAddCompanyModal(false)}
                        onAddCustomer={handleAddCustomer}
                        setToastShow={setToastShow}
                        setToastType={setToastType}
                        setToastMessage={setToastMessage}
                    />
                )
            }
            {/* Get KYC Overview Companies Modal */}
            <GetKYCOverviewCompaniesModal
                show={showKYCOverviewModal}
                close={() => setShowKYCOverview(false)}
                setCustomers={setCustomers}
                selectedCustomers={customers instanceof Error || !customers ? [] : customers.filter(c => c.checked)}
                companyKYCPrice={companyKYCPrice}
                clearChecked={() => { checkAll(false); }}
            />
            {/* Edit Contact Modal: render only when open to fully unmount on close */}
            {editContactModal.isOpen && (
                <EditContactModal
                    show={editContactModal.isOpen}
                    close={() => handleCloseEditContactModal()}
                    data={editContactModal.data}
                    contacts={editContactModal.contacts}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    customers={customers}
                    setCustomers={setCustomers}
                    employees={editContactModal.contacts?.employees ?? employees}
                />
            )}
            {/* Send Notification Modal */}
            <SendNotificationModal
                show={sendNotificationModal_isOpen}
                employees={employees}
                close={() => setSendNotificationModal_isOpen(false)}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                customers={customers}
                setCustomers={setCustomers}
            />
            {/* Risk Score Modal */}
            <RiskScoreModal
                show={riskScoreModal_isOpen}
                close={() => setRiskScoreModal_isOpen(false)}
                data={riskScoreModal_data}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                customers={customers}
                setCustomers={setCustomers}
            />
            {/* Auto Risk Score Modal */}
            <AutoRiskScoreModalCompany
                show={autoRiskScoreModel_isOpen}
                close={() => setAutoRiskScoreModel_isOpen(false)}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                customers={customers}
                setCustomers={setCustomers}
                type='company'
            />
            {/* Toast notifications */}
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </>
    );
}

export default CompaniesTable;